import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Article } from '../utils/api';
import { useLanguageStore } from '../stores/language';

export type CardSize = 'compact' | 'large';

interface ArticleCardProps {
  article: Article;
  size?: CardSize;
}

export function ArticleCard({ article, size = 'large' }: ArticleCardProps) {
  const date = article.published_at || article.created_at;
  const { language } = useLanguageStore();

  // Use Chinese title if available and user prefers Chinese
  const displayTitle = (language === 'zh' && article.title_zh) ? article.title_zh : article.title;
  const isCompact = size === 'compact';

  return (
    <article className={`card overflow-hidden hover:shadow-md dark:hover:shadow-gray-900/50 transition-shadow duration-200 relative ${
      isCompact ? 'h-40 sm:h-44' : 'h-full'
    }`}>
      {/* Pinned badge */}
      {article.is_pinned === 1 && (
        <div className="absolute top-2 right-2 z-10 bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path d="M5 5a2 2 0 012-2h6a2 2 0 012 2v2a2 2 0 01-2 2H7a2 2 0 01-2-2V5z" />
            <path d="M10 12a1 1 0 011 1v4a1 1 0 11-2 0v-4a1 1 0 011-1z" />
          </svg>
          Pinned
        </div>
      )}
      <Link to={`/article/${article.slug}`} className={isCompact ? 'flex h-full' : 'flex flex-col h-full'}>
        {article.cover_image ? (
          <div className={`bg-gray-100 dark:bg-gray-700 overflow-hidden flex-shrink-0 ${
            isCompact ? 'w-48 h-40 sm:w-56 sm:h-44' : 'aspect-video'
          }`}>
            <img
              src={article.cover_image}
              alt={article.title}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement?.classList.add('hidden');
              }}
            />
          </div>
        ) : null}
        <div className={isCompact ? 'p-4 flex-1 min-w-0 flex flex-col' : 'p-5 flex-1 flex flex-col'}>
          <h2 className={`font-bold text-gray-900 dark:text-gray-100 ${
            isCompact ? 'text-base mb-1 line-clamp-1' : 'text-xl mb-2 line-clamp-2'
          }`}>
            {displayTitle}
          </h2>
          {article.excerpt && !isCompact && (
            <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-3">{article.excerpt}</p>
          )}
          {article.excerpt && isCompact && (
            <p className="text-gray-600 dark:text-gray-400 text-sm mb-2 line-clamp-2">{article.excerpt}</p>
          )}

          {/* Compact view: author at top, tags and time at bottom */}
          {isCompact && (
            <>
              <div className="flex items-center gap-2 mb-2">
                {article.author_avatar ? (
                  <img
                    src={article.author_avatar}
                    alt={article.author_name}
                    className="w-5 h-5 rounded-full"
                  />
                ) : (
                  <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                    <span className="font-medium text-gray-600 dark:text-gray-400 text-[10px]">
                      {article.author_name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <span className="text-gray-600 dark:text-gray-400 text-xs">
                  {article.author_name}
                </span>
              </div>
              <div className="mt-auto flex items-end justify-between">
                {article.parsedTags && article.parsedTags.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {article.parsedTags.slice(0, 2).map((tag) => (
                      <span key={tag} className="badge-gray text-xs py-0 px-1.5">
                        #{tag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div />
                )}
                <time className="text-gray-500 dark:text-gray-400 text-xs">
                  {formatDistanceToNow(new Date(date), { addSuffix: true })}
                </time>
              </div>
            </>
          )}

          {/* Large view: author at top, tags and time at bottom */}
          {!isCompact && (
            <>
              <div className="flex items-center gap-2 mb-3">
                {article.author_avatar ? (
                  <img
                    src={article.author_avatar}
                    alt={article.author_name}
                    className="w-6 h-6 rounded-full"
                  />
                ) : (
                  <div className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                    <span className="font-medium text-gray-600 dark:text-gray-400 text-xs">
                      {article.author_name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <span className="text-gray-600 dark:text-gray-400 text-sm">
                  {article.author_name}
                </span>
              </div>
              <div className="mt-auto flex items-end justify-between">
                {article.parsedTags && article.parsedTags.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {article.parsedTags.slice(0, 3).map((tag) => (
                      <span key={tag} className="badge-gray">
                        #{tag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div />
                )}
                <time className="text-gray-500 dark:text-gray-400 text-sm">
                  {formatDistanceToNow(new Date(date), { addSuffix: true })}
                </time>
              </div>
            </>
          )}

        </div>
      </Link>
    </article>
  );
}
